import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { askAI } from './ai';
import { Field, Input, Select, FormGrid, SectionLabel, SaveMessage, Card, CardHeader, SubmitBtn, SecondaryBtn, EmptyState, RecordMeta, Badge, Avatar, STATES, PARTNER_TYPES, PARTNER_STATUSES } from './Shared';

const STATUS_VARIANTS = { prospect:'gray', outreach:'amber', meeting:'blue', active:'green' };
const STATUS_LABELS = { prospect:'Prospect', outreach:'Outreach sent', meeting:'Meeting scheduled', active:'Active partner' };

export default function ReferralsPage({ profile }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ company_name:'', partner_type:'Sprinkler contractor', contact_name:'', state:'', status:'prospect', jobs_referred:'0' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ show:false, text:'', error:false });
  const [aiOut, setAiOut] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showAi, setShowAi] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() {
    const { data } = await supabase.from('referral_partners').select('*').order('created_at', { ascending:false });
    setRecords(data || []);
  }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function save() {
    if (!form.company_name) { setMsg({ show:true, text:'Company name required.', error:true }); return; }
    setLoading(true);
    const { error } = await supabase.from('referral_partners').insert({ ...form, jobs_referred: parseInt(form.jobs_referred, 10)||0, submitted_by: profile.id, submitted_by_name: profile.full_name||profile.email });
    setLoading(false);
    if (error) { setMsg({ show:true, text:'Error: ' + error.message, error:true }); return; }
    setMsg({ show:true, text:'Partner added.', error:false });
    setForm({ company_name:'', partner_type:'Sprinkler contractor', contact_name:'', state:'', status:'prospect', jobs_referred:'0' });
    load();
    setTimeout(() => setMsg({ show:false }), 3000);
  }

  async function draftIntro() {
    setShowAi(true); setAiLoading(true); setAiOut('');
    const partnerList = records.map(r => `${r.company_name} (${r.partner_type}, ${r.state}, ${STATUS_LABELS[r.status]})`).join('; ') || 'none yet';
    const prompt = `Draft a short (120 words max) intro message from Alpha Omega Fire Watch to a sprinkler contractor proposing a referral partnership. Current partners: ${partnerList}. Tone: peer-to-peer, direct. Lead with value to them — referral fee, simple process, trusted partner at the exact moment fire watch is triggered. Use [COMPANY NAME] as placeholder. End with a single clear ask (15-min call).`;
    try {
      setAiOut(await askAI(prompt, 500));
    } catch (err) { setAiOut('AI error: ' + err.message); }
    setAiLoading(false);
  }

  return (
    <div>
      <Card>
        <SectionLabel>ADD REFERRAL PARTNER</SectionLabel>
        <FormGrid>
          <Field label="Company name"><Input value={form.company_name} onChange={set('company_name')} /></Field>
          <Field label="Partner type"><Select value={form.partner_type} onChange={set('partner_type')} options={PARTNER_TYPES} /></Field>
          <Field label="Primary contact"><Input value={form.contact_name} onChange={set('contact_name')} /></Field>
          <Field label="State"><Select value={form.state} onChange={set('state')} options={STATES} placeholder="Select state" /></Field>
          <Field label="Status"><Select value={form.status} onChange={set('status')} options={PARTNER_STATUSES.map(s => ({ value:s, label:STATUS_LABELS[s] }))} /></Field>
          <Field label="Jobs referred (lifetime)"><Input type="number" value={form.jobs_referred} onChange={set('jobs_referred')} /></Field>
        </FormGrid>
        <div className="form-actions">
          <SecondaryBtn onClick={draftIntro}>Draft intro →</SecondaryBtn>
          <SubmitBtn onClick={save} loading={loading}>Add partner</SubmitBtn>
        </div>
        <SaveMessage {...msg} />
      </Card>

      {showAi && (
        <div className="ai-panel">
          <div className="ai-panel-label"><div className="ai-panel-dot" />AI DRAFT — REFERRAL PARTNER INTRO</div>
          <div className="ai-panel-output" style={{ whiteSpace:'pre-wrap' }}>
            {aiLoading ? <span style={{ color:'#94A3B8' }}>Drafting intro…</span> : aiOut}
          </div>
        </div>
      )}

      <Card>
        <CardHeader title="Partner registry" hint={records.length + ' partners'} />
        {!records.length ? <EmptyState text="No referral partners yet." /> :
          records.map(r => (
            <div key={r.id} className="list-row">
              <Avatar name={r.company_name} colorClass="avatar-green" />
              <div className="list-row-info">
                <div className="list-row-name">{r.company_name}</div>
                <div className="list-row-meta">{r.partner_type} · {r.state||'—'} · Contact: {r.contact_name||'TBD'} · {r.jobs_referred} jobs referred</div>
                <RecordMeta submitter={r.submitted_by_name} date={r.created_at} />
              </div>
              <Badge variant={STATUS_VARIANTS[r.status]||'gray'}>{STATUS_LABELS[r.status]||r.status}</Badge>
            </div>
          ))
        }
      </Card>
    </div>
  );
}
